import { forwardRef, useCallback, useEffect, useRef } from 'react';
import { motion, useMotionValue, useReducedMotion, useSpring, useTransform } from 'framer-motion';
import { useCardScroll } from '../hooks/useCardScroll';
import '../styles/scroll-effects.css';

const elements = { div: motion.div, button: motion.button, article: motion.article };

export const ScrollCard = forwardRef(function ScrollCard({ as = 'div', index = 0, variant = 'card', className = '', style, children, onPointerMove, onPointerLeave, onViewportEnter, ...props }, forwardedRef) {
  const target = useRef(null);
  const reduced = useReducedMotion();
  const row = variant === 'row';
  const setRef = useCallback(node => {
    target.current = node;
    if (typeof forwardedRef === 'function') forwardedRef(node);
    else if (forwardedRef) forwardedRef.current = node;
  }, [forwardedRef]);
  const track = useCardScroll(target, reduced, row);
  const hoverX = useMotionValue(0);
  const hoverY = useMotionValue(0);
  const rotation = useTransform(() => track.rotateX.get() + hoverX.get());
  const y = useSpring(track.y, { stiffness: 125, damping: 29, mass: 0.55 });
  const rotateX = useSpring(rotation, { stiffness: 130, damping: 26 });
  const rotateY = useSpring(hoverY, { stiffness: 150, damping: 25 });
  useEffect(() => { if (reduced) { hoverX.set(0); hoverY.set(0); } }, [reduced, hoverX, hoverY]);
  const pointerMove = event => {
    if (!reduced && !row && event.pointerType === 'mouse') {
      const box = event.currentTarget.getBoundingClientRect();
      hoverX.set(-((event.clientY - box.top) / box.height - 0.5) * 5);
      hoverY.set(((event.clientX - box.left) / box.width - 0.5) * 8);
    }
    onPointerMove?.(event);
  };
  const pointerLeave = event => { hoverX.set(0); hoverY.set(0); onPointerLeave?.(event); };
  const Component = elements[as] || motion.div;
  return <Component ref={setRef} className={`scroll-card scroll-card-${variant} ${className}`} data-motion-mode={reduced ? 'reduced' : 'full'}
    style={{ ...style, y, rotateX, rotateY, transformPerspective: reduced ? undefined : 1300, '--card-delay': `${(index % 3) * 0.14}s` }}
    initial={{ opacity: reduced ? 1 : 0, scale: reduced ? 1 : row ? 0.97 : 0.86, filter: reduced ? 'blur(0px)' : row ? 'blur(2px)' : 'blur(6px)' }}
    whileInView={{ opacity: 1, scale: 1, filter: 'blur(0px)' }}
    whileHover={reduced || row ? undefined : { scale: 1.018 }} whileTap={reduced || row ? undefined : { scale: 0.985 }}
    viewport={{ once: true, amount: 0.1 }} transition={{ duration: reduced ? 0 : 1.2, delay: reduced ? 0 : (index % 3) * 0.14, ease: [0.16, 1, 0.3, 1] }}
    exit={{ opacity: 0, scale: reduced ? 1 : 0.95, transition: { duration: reduced ? 0 : 0.2 } }}
    onViewportEnter={entry => { target.current?.classList.add('scroll-card-revealed'); onViewportEnter?.(entry); }}
    onPointerMove={pointerMove} onPointerLeave={pointerLeave} {...props}
  >{children}</Component>;
});